import { useCallback } from 'react';

import { type Board } from '@air/api/types';
import { type Upload } from '@air/redux-uploader';

import { useCreateParentBoardsForUpload } from '~/components/Upload/privateUpload/useCreateParentBoardsForUpload';
import { getUploadBoardIdFromStore } from '~/components/Upload/utils/getUploadBoardIdFromStore';
import { metadataForPrivateUploadSelector } from '~/store/privateUploadMetadata/selectors';
import { useAirStore } from '~/utils/ReduxUtils';

export type PrepareForUploadingParams = {
  upload: Upload;
  onBoardCreated?: (board: Board) => void;
};

export const usePrepareForPrivateUploading = () => {
  const store = useAirStore();
  const { createParentBoardsForUpload } = useCreateParentBoardsForUpload();

  const prepareForUploading = useCallback(
    async ({ upload, onBoardCreated }: PrepareForUploadingParams) => {
      let parentBoardId: string | undefined = getUploadBoardIdFromStore({ upload, store });

      const uploadMetadata = metadataForPrivateUploadSelector(store.getState(), upload.id);
      /**
       * if the upload has an origin board or filepath, we need to enforce a parentBoardId
       * (remember the use-case that they could drag a folder into library)
       */
      const requiresParentBoardId = uploadMetadata?.originBoard || uploadMetadata?.filepath;

      if (requiresParentBoardId && !parentBoardId) {
        parentBoardId = await createParentBoardsForUpload(upload, onBoardCreated);
      }
      return parentBoardId;
    },
    [createParentBoardsForUpload, store],
  );

  return { prepareForUploading };
};
